import { Typography, IconButton, MenuItem, FormControl, FormHelperText, Button, CircularProgress, TextField } from "@mui/material"
import { Stack, Box } from "@mui/system"
import { TimeField } from "@mui/x-date-pickers"
import { FormTextField, FormSelect, getIn, useFormikContext } from "components/Formik/forms"
import FormWrapper from "components/Formik/FormWrapper"
import StandardDialog from "components/UI/Dialog/StandardDialog"
import { SavedReport, ScheduledExport } from "generated/graphql"
import CloseIcon from "landing_pages/components/svgIcons/CloseIcon"
import COLORS from "lib/colors"
import { useNotifications } from "lib/NotificationsProvider"
import { useTimezone } from "lib/TimezoneProvider"
import { isBlank, ordinalSuffix } from "lib/utils"
import { startCase, capitalize, omit, range, sortBy } from "lodash-es"
import moment from "moment"
import { ComponentProps, useCallback, useEffect, useState } from "react"
import RecipientFields from "./RecipientsField"

export type ValidationSchema = ComponentProps<typeof FormWrapper>['schema']
export type DistributionList = ScheduledExport['distributionLists'][number]

export type SaveScheduledExportArgs = {
  scheduledExport: ScheduledExport
  report: SavedReport
  isNew: boolean
}

export type SaveScheduledExport = (args: SaveScheduledExportArgs) => Promise<void>
export type DeleteScheduledExport = (id: string) => Promise<ScheduledExport>
export type LoadScheduledExports = () => Promise<ScheduledExport[]>
export type LoadDistributionLists = () => Promise<DistributionList[]>

type CreateReport<ReportConfig> = (input: {
  name: string
  reportConfig: ReportConfig
}) => Promise<SavedReport>

type UpdateReport<ReportConfig> = (input: {
  id: string
  name?: string
  reportConfig?: ReportConfig
}) => Promise<SavedReport>

type DeleteReport = (id: string) => Promise<SavedReport>


function getErrorMessage(error) {
  const networkErrors = getIn(error, 'networkError.result.errors')
  if(!Array.isArray(networkErrors)) {
    return null
  }

  if(networkErrors.some(error => error.message.includes('Validation failed: Name should be unique per user'))) {
    return 'You already have a Saved Report with that name'
  }

  return null
}

function SaveNewReportDialog({ onClose }) {
  const { values, submitForm, isSubmitting } = useFormikContext<SavedReport>()

  return(
    <StandardDialog
      title='Save as New Report'
      description={<>Do you want to save the current <strong>filters</strong>, <strong>column arrangement</strong>, and <strong>table settings</strong> as a new Saved Report?</>}
      onCancel={onClose}
      onConfirm={() => submitForm()}
      onClose={onClose}
      disabled={isBlank(values.name)}
      loading={isSubmitting}
      maxWidth='600px'
    >
      <FormTextField
        label='Report Name'
        name='name'
        required
        sx={{ pr: 7 }}
      />
    </StandardDialog>
  )
}

type SaveNewReportContainerProps<ReportConfig> = {
  initialName?: string
  onClose: () => void
  getReportConfig: () => ReportConfig
  createReport: CreateReport<ReportConfig>
  onReportCreated: (report: SavedReport) => void
  validationSchema: ValidationSchema
}

export function SaveNewReportContainer<ReportConfig>({
  initialName,
  onClose,
  getReportConfig,
  createReport,
  onReportCreated,
  validationSchema,
}: SaveNewReportContainerProps<ReportConfig>) {
  const { addNotification } = useNotifications()

  const handleSave = useCallback(async (values, { setSubmitting }) => {
    setSubmitting(true)

    try {
      const savedReport = await createReport({
        name: values.name,
        reportConfig: getReportConfig(),
      })

      addNotification({variant: 'notice', message: `"${values.name}" has been added to Saved Reports.`})
      onReportCreated(savedReport)
    } catch (error) {
      const specificErrorMessage = getErrorMessage(error)
      const generalErrorMessage = `Trouble creating "${values.name}". Please try again later.`
      addNotification({variant: 'alert', message: specificErrorMessage ?? generalErrorMessage })
    } finally {
      onClose()
      setSubmitting(false)
    }
  }, [addNotification, createReport, getReportConfig, onClose, onReportCreated])

  return(
    <FormWrapper data={{ name: initialName ?? '' }} schema={validationSchema} onSubmit={handleSave}>
      <SaveNewReportDialog onClose={onClose} />
    </FormWrapper>
  )
}

type SaveExistingReportDialogProps<ReportConfig> = {
  onClose: () => void
  selectedReport: SavedReport
  getReportConfig: () => ReportConfig
  updateReport: UpdateReport<ReportConfig>
  onReportUpdated: (report: SavedReport) => void
}

export function SaveExistingReportDialog<ReportConfig>({
  onClose,
  selectedReport,
  getReportConfig,
  updateReport,
  onReportUpdated,
}: SaveExistingReportDialogProps<ReportConfig>) {
  const [submitting, setSubmitting] = useState(false)
  const { addNotification } = useNotifications()

  const handleUpdate = useCallback(async () => {
    setSubmitting(true)

    try {
      const savedReport = await updateReport({
        id: selectedReport.id,
        reportConfig: getReportConfig(),
      })

      addNotification({variant: 'notice', message: `Saved "${selectedReport.name}".`})
      onReportUpdated(savedReport)
    } catch (error) {
      const specificErrorMessage = getErrorMessage(error)
      const generalErrorMessage = `Trouble saving "${selectedReport.name}". Please try again later.`
      addNotification({variant: 'alert', message: specificErrorMessage ?? generalErrorMessage })
    } finally {
      onClose()
      setSubmitting(false)
    }
  }, [addNotification, getReportConfig, onClose, onReportUpdated, selectedReport, updateReport])

  return(
    <StandardDialog
      title='Save Report'
      description={<>Do you want to save <strong>{selectedReport.name}</strong> with the current <strong>filters</strong>, <strong>column arrangement</strong>, and <strong>table settings</strong>?</>}
      onCancel={onClose}
      onConfirm={handleUpdate}
      onClose={onClose}
      loading={submitting}
      maxWidth='600px'
    >
    </StandardDialog>
  )
}

type InstantExport = {
  filename: string
}

export function ExportReportDialog({ onClose }) {
  const { values, submitForm, isSubmitting } = useFormikContext<InstantExport>()

  return(
    <StandardDialog
      title={`Export Performance Report`}
      onCancel={onClose}
      onConfirm={() => submitForm()}
      onClose={onClose}
      disabled={isBlank(values.filename)}
      loading={isSubmitting}
      maxWidth='600px'
    >
      <FormTextField
        label='File Name'
        name='filename'
        required
        sx={{ pr: 7, mt: 1 }}
      />
    </StandardDialog>
  )
}

type ExportReportContainerProps = {
  onClose: () => void
  reportName?: string | null
  defaultReportName: string
  handleExport: (filename: string) => void
  validationSchema: ValidationSchema
}

export function ExportReportContainer({ onClose, reportName, defaultReportName, handleExport, validationSchema }: ExportReportContainerProps) {

  const handleConfirm = useCallback((values, { setSubmitting }) => {
    setSubmitting(true)
    handleExport(values.filename)
    onClose()
    setSubmitting(false)
  }, [handleExport, onClose])

  const instantExportName = reportName || defaultReportName

  return(
    <FormWrapper data={{ filename: `${startCase(instantExportName).replace(/\s+/g, '')}.csv` }} schema={validationSchema} onSubmit={handleConfirm}>
      <ExportReportDialog onClose={onClose} />
    </FormWrapper>
  )
}

function formatExportFrequency(scheduledExport) {

  switch(scheduledExport.frequency) {
    case 'daily':
      return 'Daily'
    case 'workdays':
      return 'Every Weekday'
    case 'weekly':
      if (scheduledExport.weekCount === 1) {
        return `Every Week on [${capitalize(scheduledExport.dayOfWeek.slice(0, 3))}] Day`
      } else {
        return `Every [${scheduledExport.weekCount}] Weeks on [${capitalize(scheduledExport.dayOfWeek.slice(0, 3))}] Day`
      }
    case 'monthly':
      return `Every [${ordinalSuffix(scheduledExport.dayOfMonth)}] Day of the Month`
  }

  return null
}

function ScheduledExportsList({sortedScheduledExports, openCreateOrUpdateScheduledExport, openDeleteScheduledExport}) {

  return (
    <>
      { sortedScheduledExports.length === 0 &&
          <Typography variant="body2" sx={{ px: 4, color: COLORS.slateGray }}>
            No scheduled exports.
          </Typography>
      ||
        <>
          <Typography variant='h4'>
            Saved Scheduled Exports
          </Typography>
          <Stack>
            {sortedScheduledExports.map(scheduledExport => {
              const time = moment(scheduledExport.timeOfDay).utc().format('h:mm A')
              const groupNames = scheduledExport.distributionLists.map(list => list.name)
              const groupsAndRecipients = [...groupNames, ...scheduledExport.recipients]

              return (
                <Stack
                  direction='row'
                  onClick={() => openCreateOrUpdateScheduledExport(scheduledExport)}
                  key={scheduledExport.id}
                  sx={{
                    p: '12px 6px 12px 14px',
                    "&:hover": {
                      backgroundColor: `${COLORS.azureWhite} !important`,
                    }
                  }}
                >
                  <Box sx={{width: '100%'}}>
                    <Stack direction='row' sx={{ width: '100%', justifyContent: 'space-between' }}>
                      <Typography variant='body1' sx={{width: '33%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {scheduledExport.savedReport.name}
                      </Typography>
                      <Typography variant='body1' align='right' sx={{width: '65%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {`${formatExportFrequency(scheduledExport)}, ${time}`}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" align='right' sx={{width: '100%', color: "info.main"}}>
                      {groupsAndRecipients.join(', ')}
                    </Typography>
                  </Box>
                  <Box sx={{ml: 2, mt: '-8px'}}>
                    <IconButton aria-label='Delete Scheduled Export' sx={{ color: COLORS.cadetGray }} onClick={(e) => {openDeleteScheduledExport(scheduledExport, 'scheduledExportList'); e.stopPropagation()}} size='small'>
                      <CloseIcon/>
                    </IconButton>
                  </Box>
                </Stack>
            )})}
          </Stack>
        </>
      }
    </>
  )
}

const FREQUENCY_OPTIONS = [
  {value: 'daily', text: 'Daily'},
  {value: 'workdays', text: 'Every Weekday'},
  {value: 'weekly', text: 'Every [X] Weeks on [X] Day'},
  {value: 'monthly', text: 'Every [X] Day of the Month'},
]

const DAY_OPTIONS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function ExportFrequencySelector() {
  const { values, setFieldValue, errors, setErrors } = useFormikContext<ScheduledExport>();

  const handleFrequencySelect = useCallback((event) => {
    switch(event.target.value) {
      case 'daily':
      case 'workdays':
        setFieldValue('weekCount', null)
        setFieldValue('dayOfWeek', null)
        setFieldValue('dayOfMonth', null)
        break
      case 'weekly':
        setFieldValue('dayOfMonth', null)
        break
      case 'monthly':
        setFieldValue('weekCount', null)
        setFieldValue('dayOfWeek', null)
        break
    }

    setFieldValue('frequency', event.target.value)
  }, [setFieldValue])

  const hasTimeError = !!getIn(errors, 'timeOfDay')

  return (
    <Stack direction='row' gap={3} sx={{ '.MuiFormHelperText-root': { display: 'none'}}}>
      <TextField
        select
        value={values.frequency || ''}
        onChange={handleFrequencySelect}
        onFocus={() => setErrors(omit(errors, ['frequency']))}
        label="Frequency"
        error={!!getIn(errors, 'frequency')}
        sx={{ width: 168 }}
        id='Frequency'
      >
        {FREQUENCY_OPTIONS.map(frequency => (
          <MenuItem value={frequency.value} key={frequency.value}>{frequency.text}</MenuItem>
        ))}
      </TextField>

      {values.frequency === 'weekly' &&
        <>
          <FormSelect
            label="# Weeks"
            name="weekCount"
            sx={{ width: 74 }}
          >
            {range(1, 5).map(value => (
              <MenuItem value={value} key={value}>{value}</MenuItem>
            ))}
          </FormSelect>
          <FormSelect
            label="Day"
            name="dayOfWeek"
            sx={{ width: 94 }}
          >
            {DAY_OPTIONS.map(day => (
              <MenuItem value={day} key={day}>{capitalize(day.slice(0, 3))}</MenuItem>
            ))}
          </FormSelect>
        </>
      }

      {values.frequency === 'monthly' &&
        <FormSelect
          label="# Day"
          name="dayOfMonth"
          sx={{ width: 94 }}
        >
          {range(1, 32).map(dayOfMonth => (
            <MenuItem value={dayOfMonth} key={dayOfMonth}>{ordinalSuffix(dayOfMonth)}</MenuItem>
          ))}
        </FormSelect>
      }

      <FormControl error={hasTimeError} sx={{ width: 138 }}>
        <TimeField
          label="Time"
          value={values.timeOfDay ? moment(values.timeOfDay) : null}
          onChange={(newValue) => newValue?.isValid() && setFieldValue('timeOfDay', moment(newValue).seconds(0).milliseconds(0).format())}
          format="hh:mm A"
          timezone='UTC'
          sx={{ width: 138 }}
          InputProps={{
            error: hasTimeError,
          }}
          onFocus={() => setErrors(omit(errors, ['timeOfDay']))}
        />
        {hasTimeError && <FormHelperText>Time is required</FormHelperText>}
      </FormControl>


    </Stack>
  )
}

function CreateOrUpdateScheduledExportForm({ distributionLists }: { distributionLists: DistributionList[] }) {
  const { values, setFieldValue, errors, setErrors } = useFormikContext<ScheduledExport>()
  const { timezone } = useTimezone()

  return (
    <Stack direction='column' gap={3} mt={1}>
      <FormTextField
        label='File Name'
        name='filename'
        required
        sx={{ pr: 7 }}
      />

      <Box>
        <Typography variant='h4' mb={2}>
          Export Date(s)
        </Typography>
        <ExportFrequencySelector />
        <Typography sx={{ color: "info.main"}} variant="body2" mt={.5}>
          {`Export timezone is based on your organization (${timezone}).`}
        </Typography>
      </Box>

      <Box>
        <Typography variant='h4' mb={2}>
          Recipient(s)
        </Typography>
        <RecipientFields
          distributionLists={distributionLists}
          selectedDistributionLists={values.distributionLists ?? []}
          setSelectedDistributionLists={(lists) => setFieldValue('distributionLists', lists)}
          recipients={values.recipients ?? []}
          setRecipients={(emails) => setFieldValue('recipients', emails)}
          error={!!getIn(errors, 'recipients') || !!getIn(errors, 'distributionLists')}
          onFocus={() => setErrors(omit(errors, ['recipients', 'distributionLists']))}
          sx={{ pr: 7 }}
        />
      </Box>
    </Stack>
  )
}

function CreateOrUpdateScheduledExportDialog({ onClose, report, isNew, onDelete, distributionLists }) {
  const { submitForm, isSubmitting } = useFormikContext()

  return(
    <StandardDialog
      title={`Schedule "${report.name}" Export`}
      onCancel={onClose}
      onConfirm={() => submitForm()}
      onClose={onClose}
      maxWidth='600px'
      loading={isSubmitting}
      footer={<Button variant="outlined" onClick={onDelete} disabled={isNew}>Delete</Button>}
    >
      <CreateOrUpdateScheduledExportForm distributionLists={distributionLists} />
    </StandardDialog>
  )
}

export function CreateOrUpdateScheduledExportContainer({ onClose, selectedReport, selectedExport, openDeleteScheduledExport, saveScheduledExport, loadDistributionLists, validationSchema }) {
  const { addNotification } = useNotifications()
  const report = selectedExport.savedReport || selectedReport
  const [distributionLists, setDistributionLists] = useState<DistributionList[] | null>(null)

  useEffect(() => {
    loadDistributionLists()
      .then(setDistributionLists)
      .catch(() => {
        setDistributionLists([])
        addNotification({variant: 'alert', message: 'There was an issue loading distribution lists'})
      })
  }, [addNotification, loadDistributionLists])

  const isNew = !selectedExport?.id

  const handleSubmit = useCallback(async (values, { setSubmitting }) => {
    const actionText = isNew ? 'schedul' : 'updat'
    try {
      await saveScheduledExport({ scheduledExport: values, report, isNew })
      addNotification({variant: 'notice', message: `Your export has been ${actionText}ed`})
    } catch (error) {
      addNotification({variant: 'alert', message: `There was an issue ${actionText}ing your export`})
      console.log(error)
    } finally {
      setSubmitting(false)
      onClose()
    }
  }, [addNotification, isNew, onClose, report, saveScheduledExport])

  if(!distributionLists) {
    return <CircularProgress />
  }

  return(
    <FormWrapper data={selectedExport} schema={validationSchema} onSubmit={handleSubmit}>
      <CreateOrUpdateScheduledExportDialog onClose={onClose} report={report} isNew={isNew} onDelete={() => openDeleteScheduledExport(selectedExport, 'createOrUpdateScheduledExport')} distributionLists={distributionLists} />
    </FormWrapper>
  )
}

function ScheduledExportsListContainer({ loadScheduledExports, openCreateOrUpdateScheduledExport, openDeleteScheduledExport }) {
  const { addNotification } = useNotifications()
  const [scheduledExports, setScheduledExports] = useState<ScheduledExport[] | null>(null)

  useEffect(() => {
    loadScheduledExports()
      .then(setScheduledExports)
      .catch(() => {
        setScheduledExports([])
        addNotification({variant: 'alert', message: 'There was an issue loading scheduled exports'})
      })
  }, [addNotification, loadScheduledExports])

  if(!scheduledExports) {
    return <CircularProgress />
  }

  const sortedScheduledExports = sortBy(scheduledExports, 'id')

  return (
    <ScheduledExportsList
      sortedScheduledExports={sortedScheduledExports}
      openCreateOrUpdateScheduledExport={openCreateOrUpdateScheduledExport}
      openDeleteScheduledExport={openDeleteScheduledExport}
    />
  )
}

export function ScheduledExportsDialog({ onClose, selectedReport, loadScheduledExports, openCreateOrUpdateScheduledExport, openDeleteScheduledExport }) {

  return(
    <>
      <StandardDialog
        title='Scheduled Exports'
        description={<>All scheduled exports and their recipients are listed below.</>}
        onCancel={onClose}
        onConfirm={() => openCreateOrUpdateScheduledExport({filename: `${startCase(selectedReport.name).replace(/\s+/g, '')}.csv`})}
        disabled={!selectedReport}
        confirmText='Create New'
        tooltip={!selectedReport ? 'Selected a saved report to create new scheduled export' : ''}
        onClose={onClose}
        maxWidth='600px'
      >
        <ScheduledExportsListContainer loadScheduledExports={loadScheduledExports} openCreateOrUpdateScheduledExport={openCreateOrUpdateScheduledExport} openDeleteScheduledExport={openDeleteScheduledExport} />
      </StandardDialog>
    </>

  )
}

function RenameReportDialog({ onClose, selectedReport }) {
  const { values, submitForm, isSubmitting, dirty } = useFormikContext<SavedReport>()

  return(
    <StandardDialog
      title='Rename Report'
      description={<>Do you want to rename <strong>{selectedReport.name}</strong>?</>}
      onCancel={onClose}
      onConfirm={() => submitForm()}
      onClose={onClose}
      disabled={isBlank(values.name) || !dirty}
      loading={isSubmitting}
      maxWidth='600px'
    >
      <FormTextField
        label='Report Name'
        name='name'
        required
        sx={{ pr: 7 }}
      />
    </StandardDialog>
  )
}

type RenameReportContainerProps<ReportConfig> = {
  onClose: () => void
  selectedReport: SavedReport
  updateReport: UpdateReport<ReportConfig>
  onReportRenamed: (report: SavedReport) => void
  validationSchema: ValidationSchema
}

export function RenameReportContainer<ReportConfig>({
  onClose,
  selectedReport,
  updateReport,
  onReportRenamed,
  validationSchema,
}: RenameReportContainerProps<ReportConfig>) {
  const { addNotification } = useNotifications()

  const handleRename = useCallback(async (values, { setSubmitting }) => {
    setSubmitting(true)

    // Use selectedReport here to avoid updating properties besides the name (in case there are other changes presently)
    try {
      const savedReport = await updateReport({
        id: selectedReport.id,
        name: values.name,
      })

      addNotification({variant: 'notice', message: `Saved "${values.name}".`})
      onReportRenamed(savedReport)
    } catch (error) {
      const specificErrorMessage = getErrorMessage(error)
      const generalErrorMessage = `Trouble renaming "${selectedReport.name}". Please try again later.`
      addNotification({variant: 'alert', message: specificErrorMessage ?? generalErrorMessage })
    } finally {
      onClose()
      setSubmitting(false)
    }
  }, [addNotification, onClose, onReportRenamed, selectedReport, updateReport])

  return(
    <FormWrapper data={{ name: selectedReport.name || '' }} schema={validationSchema} onSubmit={handleRename}>
      <RenameReportDialog onClose={onClose} selectedReport={selectedReport} />
    </FormWrapper>
  )
}

type DeleteReportDialogProps = {
  onClose: () => void
  selectedReport: SavedReport
  deleteReport: DeleteReport
  onReportDeleted: (report: SavedReport) => void
}

export function DeleteReportDialog({ onClose, selectedReport, deleteReport, onReportDeleted }: DeleteReportDialogProps) {
  const [submitting, setSubmitting] = useState(false)
  const { addNotification } = useNotifications()

  const handleDelete = useCallback(async () => {
    setSubmitting(true)

    try {
      const savedReport = await deleteReport(selectedReport.id)
      addNotification({variant: 'notice', message: `Deleted "${savedReport.name}".`})
      onReportDeleted(savedReport)
    } catch {
      addNotification({variant: 'alert', message: `Trouble deleting "${selectedReport.name}". Please try again later.`})
    } finally {
      onClose()
      setSubmitting(false)
    }
  }, [addNotification, deleteReport, onClose, onReportDeleted, selectedReport])

  return(
    <StandardDialog
      title='Delete Report'
      description={<>Do you want to delete <strong>{selectedReport.name}</strong>?</>}
      onCancel={onClose}
      confirmText={'Delete'}
      onConfirm={handleDelete}
      onClose={onClose}
      loading={submitting}
      maxWidth='600px'
    >
    </StandardDialog>
  )
}

export function DeleteExportDialog({ onClose, onSuccess, selectedExport, unsetSelectedExport, deleteScheduledExport }) {
  const [submitting, setSubmitting] = useState(false)
  const { addNotification } = useNotifications()

  const handleDelete = useCallback(async () => {
    setSubmitting(true)

    try {
      const scheduledExport = await deleteScheduledExport(selectedExport.id)
      addNotification({variant: 'notice', message: `Deleted scheduled export of '${scheduledExport.savedReport.name}'.`})
      unsetSelectedExport()
      onSuccess()
    } catch {
      addNotification({variant: 'alert', message: `Trouble deleting scheduled export of '${selectedExport.name}'. Please try again later.`})
    } finally {
      setSubmitting(false)
    }
  }, [selectedExport.id, selectedExport.name, deleteScheduledExport, addNotification, unsetSelectedExport, onSuccess])

  return(
    <StandardDialog
      title='Delete Report'
      description={<>Do you want to delete the scheduled export of <strong>{selectedExport?.savedReport?.name}</strong>?</>}
      onCancel={onClose}
      confirmText={'Delete'}
      onConfirm={handleDelete}
      onClose={onClose}
      loading={submitting}
      maxWidth='600px'
    >
    </StandardDialog>
  )
}
